import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Bell, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pushSupported, iosNeedsInstall, isStandalone, enablePush, getExistingSubscription, autoEnablePush } from "@/lib/push";

const DISMISS_KEY = "kingly-push-banner-dismissed";

export const PushBanner = () => {
  const [show, setShow] = useState(false);
  const [needsInstall, setNeedsInstall] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isStandalone()) { autoEnablePush(); return; }
    autoEnablePush();
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (iosNeedsInstall()) { setNeedsInstall(true); setShow(true); return; }
    if (!pushSupported() || Notification.permission !== "default") return;
    getExistingSubscription().then((sub) => { if (!sub) setShow(true); }).catch(() => {});
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled on this device");
      setShow(false);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (!show) return null;
  return (
    <div className="mx-4 sm:mx-6 md:mx-8 mt-4 max-w-7xl md:mx-auto" data-testid="push-banner">
      <div className="flex items-center gap-3 bg-slate-900 text-white rounded-xl px-4 py-3 shadow-lg">
        <Bell className="w-4 h-4 text-amber-300 shrink-0" />
        {needsInstall ? (
          <p className="text-xs sm:text-sm flex-1">
            Get task &amp; payment alerts: tap <Share className="w-3.5 h-3.5 inline text-amber-300" /> then
            <b> &ldquo;Add to Home Screen&rdquo;</b>, open Kingly from there and enable notifications in Settings.
          </p>
        ) : (
          <p className="text-xs sm:text-sm flex-1">
            Enable push notifications for task assignments, reminders &amp; payment alerts — and skip the texts.
          </p>
        )}
        {!needsInstall && (
          <Button size="sm" onClick={enable} disabled={busy} data-testid="push-banner-enable"
            className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold shrink-0">
            {busy ? "Enabling…" : "Enable"}
          </Button>
        )}
        <button onClick={dismiss} data-testid="push-banner-dismiss" className="p-1 rounded hover:bg-white/10 shrink-0">
          <X className="w-4 h-4 text-slate-300" />
        </button>
      </div>
    </div>
  );
};
